import { COLORS, METRICS_INFO } from '../../config';
import type { EditPerformance, TaskInfo, TouchPoint, Trip } from '../../models/types';
import {
  formatDurationFromMinutes,
  formatHoursMinutes,
  formatKg,
  formatMinutesOfDay,
  getTripCapacityDelta,
  getTripStopMetrics,
  getTripTotalQtyKg,
  getTripWindowMetrics,
  getTripWorkingTimeDelta,
} from '../../utils/formatters';
import type {
  DenseCardModel,
  DenseHeaderMetricModel,
  DenseTagModel,
  SequenceRailModel,
  SequenceNodeModel,
} from './types';

type TenantVariant = 'jumbobuy' | 'gemba' | 'reworld' | 'default';

type HeaderMetricConfig = {
  key: keyof EditPerformance;
  deltaKey?: keyof EditPerformance;
  label: string;
  tooltipKey: string;
  direction: 'up-good' | 'down-good' | 'neutral';
};

type BuildTripCardOptions = {
  tenant?: string;
  isVerifyGroup?: boolean;
};

type BuildTouchPointCardOptions = {
  tenant?: string;
  addressOverride?: string;
  typeLabelOverride?: string;
};

const HEADER_METRICS: HeaderMetricConfig[] = [
  { key: 'noOfTrips', deltaKey: 'noOfTripsDelta', label: 'Trip #', tooltipKey: 'noOfTrips', direction: 'down-good' },
  { key: 'tpPerTrip', deltaKey: 'tpPerTripDelta', label: 'Points/trip', tooltipKey: 'tpPerTrip', direction: 'up-good' },
  { key: 'cpk', deltaKey: 'cpkDelta', label: 'CPK', tooltipKey: 'cpk', direction: 'down-good' },
  { key: 'cpTp', deltaKey: 'cpTpDelta', label: 'CPT', tooltipKey: 'cpTp', direction: 'down-good' },
  {
    key: 'noOfSlotSlippage',
    deltaKey: 'noOfSlotSlippageDelta',
    label: 'Slot breach',
    tooltipKey: 'slotSlippages',
    direction: 'down-good',
  },
  {
    key: 'tpsWithoutTrip',
    deltaKey: 'tpsWithoutTripDelta',
    label: 'Unallocated',
    tooltipKey: 'tpsWithoutTrip',
    direction: 'down-good',
  },
];

const INTEGER_METRIC_KEYS = new Set<string>([
  'noOfTrips',
  'tpsWithoutTrip',
  'noOfSlotSlippage',
  'tripsWithTimeBreach',
]);

const HOURS_PER_DAY = 24;

export function getTenantVariant(tenant?: string): TenantVariant {
  const normalized = String(tenant ?? '').toLowerCase();
  if (normalized.includes('jumbobuy')) return 'jumbobuy';
  if (normalized.includes('gemba')) return 'gemba';
  if (normalized.includes('reworld')) return 'reworld';
  return 'default';
}

export function getTripAccentColor(trip: Trip): string {
  const status = String(trip.tripMeta?.status ?? '').toUpperCase();
  if (status === 'UNALLOCATED') {
    return '#f59e0b';
  }
  const hash = Math.abs(
    trip.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  );
  return COLORS.tripColors[hash % COLORS.tripColors.length];
}

export function buildHeaderMetricModels(taskInfo: TaskInfo | null): DenseHeaderMetricModel[] {
  if (!taskInfo) return [];
  const performance = taskInfo.editPerf;

  return HEADER_METRICS
    .filter((item) => Number.isFinite(performance[item.key]))
    .map((item) => {
      const rawValue = Number(performance[item.key]);
      const rawDelta = item.deltaKey ? Number(performance[item.deltaKey]) : Number.NaN;
      const tooltipMeta = (METRICS_INFO as Record<string, { title: string; description: string }>)[item.tooltipKey];
      return {
        key: item.key,
        label: item.label,
        value: formatHeaderMetricValue(item.key, rawValue),
        deltaText: Number.isFinite(rawDelta) ? formatHeaderDelta(item.key, rawDelta) : undefined,
        deltaValue: Number.isFinite(rawDelta) ? rawDelta : undefined,
        deltaDirection: item.direction,
        tooltipTitle: tooltipMeta?.title ?? item.label,
        tooltipDescription: tooltipMeta?.description ?? item.label,
      };
    });
}

export function buildTripCardModel(trip: Trip, options: BuildTripCardOptions = {}): DenseCardModel {
  const tenantVariant = getTenantVariant(options.tenant);
  const isVerifyGroup = options.isVerifyGroup || String(trip.tripMeta?.status ?? '').toUpperCase() === 'VERIFY_GROUP';
  if (isVerifyGroup) {
    return buildVerifyGroupTripCardModel(trip);
  }
  if (tenantVariant === 'jumbobuy') {
    return buildJumboBuyTripCardModel(trip);
  }
  return buildGenericTripCardModel(trip);
}

export function buildTouchPointCardModel(
  touchPoint: TouchPoint,
  options: BuildTouchPointCardOptions = {}
): DenseCardModel {
  const tenantVariant = getTenantVariant(options.tenant);
  if (tenantVariant === 'jumbobuy') {
    return buildJumboBuyTouchPointCardModel(touchPoint, options);
  }
  return buildGenericTouchPointCardModel(touchPoint, options);
}

export function buildTripSequenceModel(trip: Trip, tenant?: string): SequenceRailModel {
  const tenantVariant = getTenantVariant(tenant);
  const extra = getTripExtra(trip);
  const startLabel =
    normalizeClockLabel(stringValue(extra.tripStartTime)) ||
    getTripWindowMetrics(trip).startLabel;
  const endLabel =
    normalizeClockLabel(stringValue(extra.tripEndTime)) ||
    getTripWindowMetrics(trip).endLabel;

  const points = trip.tpDetails
    .slice()
    .sort((left, right) => left.seqNo - right.seqNo)
    .map((touchPoint) => buildSequenceNode(touchPoint, tenantVariant));

  const totalWeight = points.reduce((sum, point) => sum + point.widthWeight, 0);
  const normalizedNodes = points.map((point) => ({
    ...point,
    widthWeight:
      totalWeight > 0
        ? Math.max(0.8, (point.widthWeight / totalWeight) * 12)
        : 1,
  }));

  return {
    startLabel: hasMeaningfulLabel(startLabel) ? startLabel : undefined,
    endLabel: hasMeaningfulLabel(endLabel) ? endLabel : undefined,
    nodes: normalizedNodes,
  };
}

function buildVerifyGroupTripCardModel(trip: Trip): DenseCardModel {
  const buyerCount = Number(trip.tripMeta?.extra?.buyerCount ?? trip.tpDetails.length);
  const pinCode = stringValue(trip.tripMeta?.extra?.pinCode) || trip.name.replace(/^PIN-/, '');
  const footerText = trip.tpDetails
    .slice(0, 5)
    .map((touchPoint) => touchPoint.name || touchPoint.orderNumber || touchPoint.tpId)
    .join(' • ');

  return {
    title: pinCode,
    subtitle: 'Pincode cluster',
    topTags: [
      { key: 'pin', label: 'Verify', icon: 'pin', tone: 'info', emphasis: 'micro' },
    ],
    highlightTags: [
      { key: 'weight', label: formatKg(getTripTotalQtyKg(trip)), icon: 'weight', tone: 'good', emphasis: 'strong' },
      { key: 'buyers', label: `${buyerCount} buyers`, icon: 'touchPoint', tone: 'info', emphasis: 'soft' },
    ],
    primaryMetrics: [
      {
        key: 'touchpoints',
        icon: 'touchPoint',
        value: String(trip.tpDetails.length),
        unit: 'pts',
        tone: 'info',
      },
      {
        key: 'weight',
        icon: 'weight',
        value: String(Math.round(getTripTotalQtyKg(trip))),
        unit: 'kg',
        tone: 'good',
      },
    ],
    capacityTags: [],
    breachTags: [],
    footerTags: [],
    footerText,
  };
}

function buildJumboBuyTripCardModel(trip: Trip): DenseCardModel {
  const extra = getTripExtra(trip);
  const tripIdentifier = stringValue(trip.tripMeta?.loadId) || String(trip.vehTripNo || '');
  const zoneId = stringValue(extra.zoneId);
  const startTime = normalizeClockLabel(stringValue(extra.tripStartTime));
  const available = extractAvailableCapacity(trip);
  const breachTags = buildTripBreachTags(trip);
  const footerPins = compactFooterPinCodes(trip);
  const toteSummary = buildToteSummary(trip);
  const pointCount = trip.tpDetails.length;

  const highlightTags: DenseTagModel[] = [
    maybeTag('gmv', `${compactCurrency(trip.tripCostPerf.gmv)} gmv`, 'gmv', 'info', 'soft'),
    maybeTag('ctoGmv', `${percentageText(trip.tripCostPerf.ctoGmv)} c%gmv`, 'percentage', 'info', 'soft'),
    maybeTag('cpk', `${compactCurrency(trip.tripCostPerf.cpk, 2)} cpk`, 'cost', 'info', 'soft'),
    maybeTag('cpt', `${compactCurrency(trip.tripCostPerf.cpt, 0)} cpt`, 'cost', 'info', 'soft'),
    startTime ? { key: 'start', label: startTime, icon: 'time', tone: 'neutral', emphasis: 'soft' } : null,
  ].filter(Boolean) as DenseTagModel[];

  const footerTags: DenseTagModel[] = [
    zoneId ? { key: 'zone', label: zoneId, icon: 'cluster', tone: 'neutral', emphasis: 'micro' } : null,
    pointCount > 0
      ? { key: 'buyers', label: `${pointCount} buyers`, icon: 'touchPoint', tone: 'neutral', emphasis: 'micro' }
      : null,
  ].filter(Boolean) as DenseTagModel[];

  const primaryMetrics = [
    {
      key: 'weight',
      icon: 'weight' as const,
      value: String(Math.round(getTripTotalQtyKg(trip))),
      unit: 'kg',
      tone: 'good' as const,
    },
    {
      key: 'points',
      icon: 'touchPoint' as const,
      value: String(pointCount),
      unit: 'pts',
      tone: 'info' as const,
    },
    {
      key: 'distance',
      icon: 'distance' as const,
      value: String(Math.round(Math.max(0, trip.distKm))),
      unit: 'km',
      tone: 'neutral' as const,
    },
    toteSummary
      ? {
          key: 'totes',
          icon: 'tote' as const,
          value: toteSummary.value,
          unit: toteSummary.unit,
          tone: 'accent' as const,
        }
      : null,
    {
      key: 'duration',
      icon: 'duration' as const,
      value: normalizeDurationLabel(stringValue(extra.travelTime)) || getTripWindowMetrics(trip).durationLabel,
      tone: 'neutral' as const,
    },
    trip.volCuFt > 0
      ? {
          key: 'volume',
          icon: 'volume' as const,
          value: String(Math.round(trip.volCuFt)),
          unit: 'cuft',
          tone: 'neutral' as const,
        }
      : null,
  ].filter(Boolean) as DenseCardModel['primaryMetrics'];

  const capacityTags: DenseTagModel[] = [
    maybeCapacityTag('avl-kg', available.kgs, 'weight', 'kg'),
    maybeCapacityTag('avl-pts', available.touchPoints, 'touchPoint', 'pts'),
    maybeCapacityTag('avl-km', available.kms, 'distance', 'km'),
    maybeCapacityTag('avl-vol', available.volCuFt, 'volume', 'cuft'),
    maybeCapacityTag('avl-crates', available.crates, 'tote', 'crates'),
  ].filter(Boolean) as DenseTagModel[];

  return {
    title: trip.name,
    subtitle: trip.vehDetails.vehId && trip.vehDetails.vehId !== trip.name ? trip.vehDetails.vehId : undefined,
    topTags: [
      tripIdentifier
        ? { key: 'trip-id', label: `#${tripIdentifier}`, icon: 'vehicle', tone: 'accent', emphasis: 'strong' }
        : null,
      ...trip.slotTags.slice(0, 2).map((tag, index) => ({
        key: `slot-${index}`,
        label: tag,
        icon: 'slot' as const,
        tone: 'neutral' as const,
        emphasis: 'micro' as const,
      })),
    ].filter(Boolean) as DenseTagModel[],
    highlightTags,
    primaryMetrics,
    capacityTags,
    breachTags,
    footerTags,
    footerText: footerPins,
  };
}

function buildGenericTripCardModel(trip: Trip): DenseCardModel {
  const tripWindow = getTripWindowMetrics(trip);
  const availableWeight = getTripCapacityDelta(trip);
  const availableTime = getTripWorkingTimeDelta(trip);
  const stopMetrics = getTripStopMetrics(trip);
  const footerTags: DenseTagModel[] = [];

  if (trip.assignmentMeta?.routes?.length) {
    footerTags.push({
      key: 'routes',
      label: trip.assignmentMeta.routes.join(' • '),
      icon: 'cluster',
      tone: 'neutral',
      emphasis: 'micro',
      maxWidth: 220,
      tooltip: trip.assignmentMeta.routes.join(' | '),
    });
  }

  return {
    title: trip.name,
    subtitle: trip.vehDetails.vehId && trip.vehDetails.vehId !== trip.name ? trip.vehDetails.vehId : undefined,
    topTags: [
      trip.tripMeta?.status
        ? {
            key: 'status',
            label: trip.tripMeta.status,
            icon: 'slot',
            tone: String(trip.tripMeta.status).toUpperCase() === 'UNALLOCATED' ? 'warn' : 'neutral',
            emphasis: 'micro',
          }
        : null,
    ].filter(Boolean) as DenseTagModel[],
    highlightTags: [
      {
        key: 'window',
        label: tripWindow.windowLabel,
        icon: 'time',
        tone: 'info',
        emphasis: 'soft',
      },
      {
        key: 'duration',
        label: tripWindow.durationLabel,
        icon: 'duration',
        tone: 'neutral',
        emphasis: 'soft',
      },
    ],
    primaryMetrics: [
      {
        key: 'weight',
        value: String(Math.round(getTripTotalQtyKg(trip))),
        unit: 'kg',
        icon: 'weight',
        tone: 'good',
      },
      {
        key: 'pickups',
        value: `${stopMetrics.pickups}/${stopMetrics.drops}`,
        unit: 'P/D',
        icon: 'touchPoint',
        tone: 'info',
      },
      {
        key: 'distance',
        value: String(Math.round(Math.max(0, trip.distKm))),
        unit: 'km',
        icon: 'distance',
        tone: 'neutral',
      },
      availableWeight
        ? {
            key: 'available-weight',
            value: formatAvailableDelta(availableWeight.delta, 'kg'),
            icon: 'capacity',
            tone: availableWeight.isOver ? 'danger' : 'good',
            detail: 'weight left',
          }
        : null,
      availableTime
        ? {
            key: 'available-time',
            value: formatHoursMinutes(Math.abs(availableTime.delta), availableTime.isOver),
            icon: 'duration',
            tone: availableTime.isOver ? 'danger' : 'good',
            detail: 'time left',
          }
        : null,
    ].filter(Boolean) as DenseCardModel['primaryMetrics'],
    capacityTags: [],
    breachTags: buildTripBreachTags(trip),
    footerTags,
    footerText: undefined,
  };
}

function buildJumboBuyTouchPointCardModel(
  touchPoint: TouchPoint,
  options: BuildTouchPointCardOptions
): DenseCardModel {
  const extra = getTouchPointExtra(touchPoint);
  const weightKg = getTouchPointWeightKg(touchPoint);
  const dwellMinutes = getTouchPointDwellMinutes(touchPoint);
  const deliveryTime = getTouchPointDeliveryTime(touchPoint);
  const windowLabel = compactWindowLabel(getTouchPointWindowLabel(touchPoint));
  const toteCountTag = stringValue(extra.toteCountTag);
  const floorNumber = stringValue(extra.floorNumber);
  const locationQualityTag = buildTouchPointLocationQualityTag(touchPoint);

  return {
    title: touchPoint.name || touchPoint.tpId,
    subtitle: options.addressOverride ?? touchPoint.address,
    topTags: [],
    highlightTags: [
      deliveryTime
        ? { key: 'delivery', label: deliveryTime, icon: 'time', tone: 'danger', emphasis: 'soft' }
        : null,
      dwellMinutes != null
        ? { key: 'dwell', label: `${formatDurationFromMinutes(dwellMinutes)}`, icon: 'duration', tone: 'neutral', emphasis: 'soft' }
        : null,
      windowLabel
        ? { key: 'window', label: windowLabel, icon: 'slot', tone: 'info', emphasis: 'soft' }
        : null,
    ].filter(Boolean) as DenseTagModel[],
    primaryMetrics: [
      {
        key: 'weight',
        icon: 'weight',
        value: String(Math.round(weightKg)),
        unit: 'kg',
        tone: 'good',
      },
      toteCountTag
        ? {
            key: 'totes',
            icon: 'tote',
            value: toteCountTag,
            unit: 'totes',
            tone: 'accent',
          }
        : touchPoint.itemSummary.dropItemCount > 0
          ? {
              key: 'items',
              icon: 'tote',
              value: String(touchPoint.itemSummary.dropItemCount),
              unit: 'items',
              tone: 'accent',
            }
          : null,
      touchPoint.itemSummary.volCuFt > 0
        ? {
            key: 'volume',
            icon: 'volume',
            value: String(Math.round(touchPoint.itemSummary.volCuFt)),
            unit: 'cuft',
            tone: 'neutral',
          }
        : null,
    ].filter(Boolean) as DenseCardModel['primaryMetrics'],
    capacityTags: [
      touchPoint.zipCode
        ? { key: 'pin', label: touchPoint.zipCode, icon: 'pin', tone: 'neutral', emphasis: 'micro' }
        : null,
      touchPoint.itemSummary.gmv > 0
        ? { key: 'gmv', label: compactCurrency(touchPoint.itemSummary.gmv), icon: 'gmv', tone: 'info', emphasis: 'micro' }
        : null,
    ].filter(Boolean) as DenseTagModel[],
    breachTags: touchPoint.timeBreachMin > 0
      ? [
          {
            key: 'time-breach',
            label: `+${touchPoint.timeBreachMin}m`,
            icon: 'warning',
            tone: 'danger',
            emphasis: 'soft',
          },
        ]
      : [],
    footerTags: [
      floorNumber
        ? { key: 'floor', label: `Floor ${floorNumber}`, icon: 'floor', tone: 'neutral', emphasis: 'micro' }
        : null,
      locationQualityTag,
    ].filter(Boolean) as DenseTagModel[],
    footerText: undefined,
  };
}

function buildGenericTouchPointCardModel(
  touchPoint: TouchPoint,
  options: BuildTouchPointCardOptions
): DenseCardModel {
  const weightKg = getTouchPointWeightKg(touchPoint);
  const windowLabel = compactWindowLabel(getTouchPointWindowLabel(touchPoint));
  const deliveryTime = getTouchPointDeliveryTime(touchPoint);
  const floorNumber = stringValue(getTouchPointExtra(touchPoint).floorNumber);
  const locationQualityTag = buildTouchPointLocationQualityTag(touchPoint);
  const footerText = joinFooterText(touchPoint.customerName, touchPoint.materialDescription);

  return {
    title: touchPoint.name || touchPoint.tpId,
    subtitle: options.addressOverride ?? touchPoint.address,
    topTags: [],
    highlightTags: [
      windowLabel
        ? { key: 'window', label: windowLabel, icon: 'slot', tone: 'info', emphasis: 'soft' }
        : null,
      deliveryTime
        ? { key: 'delivery', label: deliveryTime, icon: 'time', tone: 'neutral', emphasis: 'soft' }
        : null,
    ].filter(Boolean) as DenseTagModel[],
    primaryMetrics: [
      {
        key: 'weight',
        icon: 'weight',
        value: String(Math.round(weightKg)),
        unit: 'kg',
        tone: 'good',
      },
      touchPoint.itemSummary.volCuFt > 0
        ? {
            key: 'volume',
            icon: 'volume',
            value: String(Math.round(touchPoint.itemSummary.volCuFt)),
            unit: 'cuft',
            tone: 'neutral',
          }
        : null,
    ].filter(Boolean) as DenseCardModel['primaryMetrics'],
    capacityTags: [
      options.typeLabelOverride
        ? { key: 'type', label: options.typeLabelOverride, icon: 'touchPoint', tone: 'neutral', emphasis: 'micro' }
        : null,
      touchPoint.orderNumber
        ? { key: 'order', label: touchPoint.orderNumber, icon: 'order', tone: 'neutral', emphasis: 'micro' }
        : null,
      touchPoint.zipCode
        ? { key: 'pin', label: touchPoint.zipCode, icon: 'pin', tone: 'neutral', emphasis: 'micro' }
        : null,
      touchPoint.itemSummary.gmv > 0
        ? { key: 'gmv', label: compactCurrency(touchPoint.itemSummary.gmv), icon: 'gmv', tone: 'info', emphasis: 'micro' }
        : null,
    ].filter(Boolean) as DenseTagModel[],
    breachTags: touchPoint.timeBreachMin > 0
      ? [
          {
            key: 'time-breach',
            label: `+${touchPoint.timeBreachMin}m`,
            icon: 'warning',
            tone: 'danger',
            emphasis: 'soft',
          },
        ]
      : [],
    footerTags: [
      floorNumber
        ? { key: 'floor', label: `Floor ${floorNumber}`, icon: 'floor', tone: 'neutral', emphasis: 'micro' }
        : null,
      locationQualityTag,
    ].filter(Boolean) as DenseTagModel[],
    footerText,
  };
}

function buildSequenceNode(touchPoint: TouchPoint, tenantVariant: TenantVariant): SequenceNodeModel {
  const weightKg = Math.max(0, getTouchPointWeightKg(touchPoint));
  const breach = touchPoint.timeBreachMin > 0;
  const primary =
    tenantVariant === 'jumbobuy'
      ? compactHourLabel(getTouchPointDeliveryTime(touchPoint) || getTouchPointWindowStartLabel(touchPoint) || String(touchPoint.seqNo))
      : String(touchPoint.seqNo).padStart(2, '0');
  const secondary =
    tenantVariant === 'jumbobuy'
      ? weightKg > 0
        ? `${Math.round(weightKg)}kg`
        : firstNonEmpty(stringValue(getTouchPointExtra(touchPoint).floorNumber), touchPoint.name)
      : firstNonEmpty(touchPoint.name, touchPoint.orderNumber, touchPoint.tpId);

  return {
    key: `${touchPoint.tpId}-${touchPoint.seqNo}`,
    primary,
    secondary,
    widthWeight: weightKg > 0 ? weightKg : 1,
    tone: breach ? 'danger' : 'neutral',
    hasBreach: breach,
    tooltip: [touchPoint.name || touchPoint.tpId, touchPoint.address, getTouchPointWindowLabel(touchPoint)]
      .filter(Boolean)
      .join(' • '),
  };
}

function buildTripBreachTags(trip: Trip): DenseTagModel[] {
  const breaches: DenseTagModel[] = [];
  if (trip.breachesAndRatios.wtBreachKg > 0) {
    breaches.push({
      key: 'wt-breach',
      label: `+${Math.round(trip.breachesAndRatios.wtBreachKg)} kg`,
      icon: 'weight',
      tone: 'danger',
      emphasis: 'soft',
    });
  }
  if (trip.breachesAndRatios.volBreachCuFt > 0) {
    breaches.push({
      key: 'vol-breach',
      label: `+${Math.round(trip.breachesAndRatios.volBreachCuFt)} cu ft`,
      icon: 'volume',
      tone: 'danger',
      emphasis: 'soft',
    });
  }
  if (trip.breachesAndRatios.distBreachKm > 0) {
    breaches.push({
      key: 'dist-breach',
      label: `+${Math.round(trip.breachesAndRatios.distBreachKm)} km`,
      icon: 'distance',
      tone: 'danger',
      emphasis: 'soft',
    });
  }
  if (trip.breachesAndRatios.slotBreaches > 0) {
    breaches.push({
      key: 'slot-breach',
      label: `+${trip.breachesAndRatios.slotBreaches} slots`,
      icon: 'warning',
      tone: 'danger',
      emphasis: 'soft',
    });
  }
  if (trip.breachesAndRatios.timeBreachMin > 0) {
    breaches.push({
      key: 'time-breach',
      label: `+${formatDurationFromMinutes(trip.breachesAndRatios.timeBreachMin)}`,
      icon: 'duration',
      tone: 'danger',
      emphasis: 'soft',
    });
  }
  return breaches;
}

function buildToteSummary(trip: Trip): { value: string; unit?: string } | null {
  const toteTags = trip.tpDetails
    .map((touchPoint) => stringValue(getTouchPointExtra(touchPoint).toteCountTag))
    .filter((value): value is string => Boolean(value && /[A-Za-z1-9]/.test(value)));

  if (toteTags.length > 0) {
    const normalized = Array.from(new Set(toteTags));
    return {
      value: normalized[0],
      unit: normalized.length > 1 ? `+${normalized.length - 1}` : 'totes',
    };
  }

  const totalItems = trip.tpDetails.reduce(
    (sum, touchPoint) => sum + Math.max(0, touchPoint.itemSummary.dropItemCount),
    0
  );
  if (totalItems > 0) {
    return {
      value: String(totalItems),
      unit: 'items',
    };
  }
  return null;
}

function compactFooterPinCodes(trip: Trip): string | undefined {
  const pinCodes = trip.breachesAndRatios.distinctZipCodes.filter(Boolean);
  if (!pinCodes.length) return undefined;
  if (pinCodes.length <= 5) {
    return pinCodes.join(' • ');
  }
  return `${pinCodes.slice(0, 5).join(' • ')} • +${pinCodes.length - 5}`;
}

function extractAvailableCapacity(trip: Trip): {
  kgs: number | null;
  kms: number | null;
  touchPoints: number | null;
  volCuFt: number | null;
  crates: number | null;
} {
  const extra = getTripExtra(trip);
  const available = toRecord(extra.availableCapacity);
  const derivedWeight = getTripCapacityDelta(trip);

  return {
    kgs: firstFinite(
      numberValue(available.avlKgs),
      derivedWeight?.delta
    ),
    kms: numberValue(available.avlKms),
    touchPoints: numberValue(available.avlTp),
    volCuFt: convertCcToCuFt(numberValue(available.avlVolCc)),
    crates: numberValue(available.avlCrates),
  };
}

function maybeCapacityTag(
  key: string,
  value: number | null,
  icon: DenseTagModel['icon'],
  unit: string
): DenseTagModel | null {
  if (value == null || !Number.isFinite(value)) return null;
  return {
    key,
    label: `Avl ${formatCompactCapacityValue(value)}${unit}`,
    icon,
    tone: value < 0 ? 'danger' : 'good',
    emphasis: 'micro',
  };
}

function getTripExtra(trip: Trip): Record<string, unknown> {
  return toRecord(trip.tripMeta?.extra);
}

function getTouchPointExtra(touchPoint: TouchPoint): Record<string, unknown> {
  return toRecord(touchPoint.extra);
}

function getTouchPointWeightKg(touchPoint: TouchPoint): number {
  return Math.max(0, Number(touchPoint.itemSummary.dropWtKg ?? 0)) +
    Math.max(0, Number(touchPoint.itemSummary.pickWtKg ?? 0));
}

function getTouchPointDeliveryTime(touchPoint: TouchPoint): string | undefined {
  const start = Number(touchPoint.attemptTime?.start);
  if (Number.isFinite(start) && start > 0) {
    return formatMinutesOfDay(start % (HOURS_PER_DAY * 60));
  }
  return normalizeClockLabel(touchPoint.scheduledDateTime);
}

function getTouchPointWindowStartLabel(touchPoint: TouchPoint): string | undefined {
  const start = Number(touchPoint.openHoursMinOfDay?.[0]?.start);
  if (Number.isFinite(start) && start > 0) {
    return formatMinutesOfDay(start % (HOURS_PER_DAY * 60));
  }
  const raw = normalizeClockLabel(touchPoint.timeWindowRaw);
  return raw ? raw.split(' - ')[0] : undefined;
}

function getTouchPointWindowLabel(touchPoint: TouchPoint): string | undefined {
  const start = Number(touchPoint.openHoursMinOfDay?.[0]?.start);
  const end = Number(touchPoint.openHoursMinOfDay?.[0]?.end);
  if (Number.isFinite(start) && Number.isFinite(end) && (start > 0 || end > 0)) {
    return `${formatMinutesOfDay(start % (HOURS_PER_DAY * 60))} - ${formatMinutesOfDay(end % (HOURS_PER_DAY * 60))}`;
  }
  return normalizeWindowLabel(touchPoint.timeWindowRaw);
}

function getTouchPointDwellMinutes(touchPoint: TouchPoint): number | null {
  const explicit = Number(touchPoint.deliveryDurationMinutes);
  if (Number.isFinite(explicit) && explicit > 0) {
    return explicit;
  }
  const start = Number(touchPoint.attemptTime?.start);
  const end = Number(touchPoint.attemptTime?.end);
  if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
    return end - start;
  }
  return null;
}

function buildTouchPointLocationQualityTag(touchPoint: TouchPoint): DenseTagModel | null {
  const status = touchPoint.geoStatus ?? 'original';

  switch (status) {
    case 'corrected':
      return {
        key: 'location-quality',
        label: 'Corrected',
        icon: 'location',
        tone: 'good',
        emphasis: 'micro',
      };
    case 'low_confidence':
      return {
        key: 'location-quality',
        label: 'Low confidence',
        icon: 'location',
        tone: 'warn',
        emphasis: 'micro',
      };
    case 'derived':
      return {
        key: 'location-quality',
        label: 'Derived',
        icon: 'location',
        tone: 'warn',
        emphasis: 'micro',
      };
    case 'original':
    default:
      return {
        key: 'location-quality',
        label: 'Original',
        icon: 'location',
        tone: 'neutral',
        emphasis: 'micro',
      };
  }
}

function compactCurrency(value: number, decimals = 1): string {
  if (!Number.isFinite(value)) return '₹0';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1000000) {
    return `${sign}₹${trimTrailingZeros((abs / 1000000).toFixed(decimals))}M`;
  }
  if (abs >= 1000) {
    return `${sign}₹${trimTrailingZeros((abs / 1000).toFixed(decimals))}k`;
  }
  return `${sign}₹${trimTrailingZeros(abs.toFixed(Math.min(decimals, 2)))}`;
}

function percentageText(value: number): string {
  if (!Number.isFinite(value)) return '0%';
  const normalized = value > 1 ? value : value * 100;
  return `${trimTrailingZeros(normalized.toFixed(2))}%`;
}

function formatHeaderMetricValue(key: keyof EditPerformance, value: number): string {
  if (!Number.isFinite(value)) return '0';
  if (INTEGER_METRIC_KEYS.has(key)) return String(Math.round(value));
  if (key === 'ctoGmv') return trimTrailingZeros(value.toFixed(2));
  if (key === 'cpk' || key === 'cpTp') return trimTrailingZeros(value.toFixed(2));
  if (key === 'wtPerTripKg') return trimTrailingZeros(value.toFixed(value >= 100 ? 0 : 2));
  return trimTrailingZeros(value.toFixed(value >= 100 ? 0 : 2));
}

function formatHeaderDelta(key: keyof EditPerformance, delta: number): string {
  const prefix = delta >= 0 ? '+' : '-';
  const abs = Math.abs(delta);
  if (INTEGER_METRIC_KEYS.has(key)) {
    return `${prefix}${Math.round(abs)}`;
  }
  return `${prefix}${trimTrailingZeros(abs.toFixed(abs >= 100 ? 0 : 2))}`;
}

function normalizeClockLabel(raw?: string): string | undefined {
  const value = String(raw ?? '').trim();
  if (!value) return undefined;
  const match = value.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!match) return undefined;
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

function normalizeWindowLabel(raw?: string): string | undefined {
  const value = String(raw ?? '').trim();
  if (!value) return undefined;
  const parts = value.split('-').map((item) => item.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const left = normalizeClockLabel(parts[0]) ?? parts[0];
    const right = normalizeClockLabel(parts[1]) ?? parts[1];
    return `${left} - ${right}`;
  }
  return value;
}

function compactWindowLabel(value?: string): string | undefined {
  if (!value) return undefined;
  return value.replace(/\s*-\s*/g, '-');
}

function normalizeDurationLabel(raw?: string): string | undefined {
  const value = String(raw ?? '').trim();
  if (!value) return undefined;
  const parts = value.split(':').map((item) => Number(item));
  if (parts.length >= 2 && parts.every((item) => Number.isFinite(item))) {
    const [hours, minutes] = parts;
    return formatDurationFromMinutes((hours * 60) + minutes);
  }
  return undefined;
}

function compactHourLabel(value: string): string {
  const normalized = normalizeClockLabel(value);
  if (!normalized) return value.slice(0, 2);
  return normalized.slice(0, 2);
}

function formatAvailableDelta(delta: number, unit: string): string {
  const rounded = Math.round(Math.abs(delta));
  return `${delta < 0 ? '+' : ''}${rounded}${unit ? ` ${unit}` : ''}`;
}

function formatCompactCapacityValue(value: number): string {
  const rounded = Math.abs(value) >= 100 ? Math.round(value) : Number(value.toFixed(1));
  return trimTrailingZeros(String(rounded));
}

function convertCcToCuFt(value: number | null): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  return value / 28316.8466;
}

function trimTrailingZeros(value: string): string {
  return value.replace(/\.0+$|(\.\d*?)0+$/, '$1');
}

function maybeTag(
  key: string,
  label: string | undefined,
  icon: DenseTagModel['icon'],
  tone: DenseTagModel['tone'],
  emphasis: DenseTagModel['emphasis']
): DenseTagModel | null {
  if (!label) return null;
  return { key, label, icon, tone, emphasis };
}

function hasMeaningfulLabel(value?: string): value is string {
  return Boolean(value && value !== '—');
}

function toRecord(value: unknown): Record<string, unknown> {
  if (typeof value === 'object' && value != null) {
    return value as Record<string, unknown>;
  }
  return {};
}

function numberValue(value: unknown): number | null {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function stringValue(value: unknown): string | undefined {
  const text = String(value ?? '').trim();
  return text ? text : undefined;
}

function firstFinite(...values: Array<number | null | undefined>): number | null {
  for (const value of values) {
    if (value != null && Number.isFinite(value)) {
      return value;
    }
  }
  return null;
}

function firstNonEmpty(...values: Array<string | undefined>): string | undefined {
  return values.find((value) => Boolean(value && value.trim()));
}

function joinFooterText(...values: Array<string | undefined>): string | undefined {
  const uniqueValues = values
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .filter((value, index, allValues) => allValues.indexOf(value) === index);
  if (!uniqueValues.length) return undefined;
  return uniqueValues.join(' • ');
}
